import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { PageInfo } from './service-common';
import type { UserTypeValue } from '../core/models';

export interface Pageable {
  page: number;
  size: number;
  sort?: string[];
}

export interface UserDTO {
  id: number;
  email: string;
  type?: UserTypeValue;
  name?: string;
  lastname?: string;
  surname?: string;
  phone?: string;
  jobTitle?: string;
  avatarUrl?: string;
  signatureId?: number | null;
  signatureName?: string | null;
  createdUserId?: number;
  lastUserId?: number;
  time?: string;
  state: 'ACTIVE' | 'INACTIVE';
  createdAt?: string;
  updatedAt?: string;
}

export interface UserFilterSpec {
  [key: string]: string | number | boolean | string[] | undefined | null;
}

export interface WrapperResponseListUserDTO {
  ok: boolean;
  code: number;
  message: string;
  metadata?: unknown;
  body: UserDTO[];
  page?: PageInfo;
}

export interface WrapperResponseUserDTO {
  ok: boolean;
  code: number;
  message: string;
  metadata?: unknown;
  body: UserDTO;
}

export interface CreateUserRequest {
  email: string;
  name: string;
  password: string;
  lastname?: string;
  surname?: string;
  phone?: string;
  jobTitle?: string;
  avatarBase64?: string;
  avatarContentType?: string;
  permissions?: string[];
  roles?: string[];
}

export interface UpdateUserRequest {
  state: 'ACTIVE';
  name?: string;
  lastname?: string;
  surname?: string;
  phone?: string;
  jobTitle?: string;
  avatarBase64?: string;
  avatarContentType?: string;
  permissions?: string[];
  roles?: string[];
}

export interface UploadAvatarRequest {
  avatarBase64: string;
  avatarContentType?: string;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export interface WrapperResponseVoid {
  ok: boolean;
  code: number;
  message: string;
  metadata?: unknown;
  body?: null;
}

export interface PermissionDTO {
  value: string;
  description: string;
  global: boolean;
}

export interface WrapperResponseListPermissionDTO {
  ok: boolean;
  code: number;
  message: string;
  metadata?: unknown;
  body: PermissionDTO[];
}

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;

  getUsers(
    spec: UserFilterSpec = {},
    pageable: Pageable = { page: 0, size: 20 }
  ): Observable<WrapperResponseListUserDTO> {
    let params = new HttpParams();

    Object.entries(spec).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (Array.isArray(value)) {
        if (value.length > 0) {
          value.forEach(v => params = params.append(key, String(v)));
        }
      } else if (value !== '') {
        params = params.append(key, String(value));
      }
    });

    params = params.append('page', String(pageable.page));
    params = params.append('size', String(pageable.size));

    if (pageable.sort && pageable.sort.length > 0) {
      pageable.sort.forEach(sort => params = params.append('sort', sort));
    }

    return this.http.get<WrapperResponseListUserDTO>(`${this.apiUrl}/users`, { params });
  }

  getMe(): Observable<WrapperResponseUserDTO> {
    return this.http.get<WrapperResponseUserDTO>(`${this.apiUrl}/users/me`);
  }

  changeOwnPassword(request: ChangePasswordRequest): Observable<WrapperResponseVoid> {
    return this.http.put<WrapperResponseVoid>(`${this.apiUrl}/users/me/password`, request);
  }

  updateOwnAvatar(request: UploadAvatarRequest): Observable<WrapperResponseUserDTO> {
    return this.http.put<WrapperResponseUserDTO>(`${this.apiUrl}/users/me/avatar`, request);
  }

  updateOwnSignature(signatureId: number | null): Observable<WrapperResponseUserDTO> {
    return this.http.put<WrapperResponseUserDTO>(`${this.apiUrl}/users/me/signature`, {
      signatureId
    });
  }

  getUserById(userId: number): Observable<WrapperResponseUserDTO> {
    return this.http.get<WrapperResponseUserDTO>(`${this.apiUrl}/users/${userId}`);
  }

  getUserPermissions(userId: number): Observable<WrapperResponseListPermissionDTO> {
    return this.http.get<WrapperResponseListPermissionDTO>(`${this.apiUrl}/users/${userId}/permissions`);
  }

  deleteUser(userId: number): Observable<WrapperResponseUserDTO> {
    return this.http.delete<WrapperResponseUserDTO>(`${this.apiUrl}/users/${userId}`);
  }

  createUser(request: CreateUserRequest): Observable<WrapperResponseUserDTO> {
    return this.http.post<WrapperResponseUserDTO>(`${this.apiUrl}/users`, request);
  }

  updateUser(userId: number, request: UpdateUserRequest): Observable<WrapperResponseUserDTO> {
    return this.http.put<WrapperResponseUserDTO>(`${this.apiUrl}/users/${userId}`, request);
  }
}
